import { useRef, useState } from "react";
import { FileText, Upload, RefreshCw, Trash2 } from "lucide-react";
import { MANAGED_FILE_MAX_BYTES, fileTooLargeMessage } from "@/utils/managedFile";

interface Props {
  /** Current scan (null = none uploaded yet). */
  scan: { url: string | null; original_filename: string } | null;
  canManage: boolean;
  uploading: boolean;
  onSelect: (file: File) => void;
  /** Optional — when provided, a delete button is shown next to an existing scan. */
  onDelete?: () => void;
  /** Compact variant for table rows (icons only). */
  compact?: boolean;
  /** Why the last upload of this field failed — shown until the next pick. */
  uploadError?: string | null;
}

function isPdf(file: File) {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

/** Upload / replace field for a contract scan (PDF only). */
export function ContractScanField({
  scan,
  canManage,
  uploading,
  onSelect,
  onDelete,
  compact = false,
  uploadError = null,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePick = (file: File | undefined) => {
    if (!file) return;
    if (!isPdf(file)) {
      setError("Dozwolony jest wyłącznie plik PDF.");
      return;
    }
    if (file.size > MANAGED_FILE_MAX_BYTES) {
      setError(fileTooLargeMessage(file));
      return;
    }
    setError(null);
    onSelect(file);
  };

  const pickButtonTitle = scan ? "Podmień skan (PDF)" : "Dodaj skan (PDF)";

  return (
    <div className={compact ? "inline-flex items-center gap-1" : "flex items-center gap-2 flex-wrap"}>
      {scan ? (
        scan.url ? (
          <a
            href={scan.url}
            target="_blank"
            rel="noreferrer"
            title={scan.original_filename}
            className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline max-w-[180px]"
          >
            <FileText size={13} className="flex-shrink-0" />
            <span className="truncate">
              {compact ? "Skan" : scan.original_filename}
            </span>
          </a>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400" title="Plik niedostępny">
            <FileText size={13} /> {compact ? "Skan" : scan.original_filename}
          </span>
        )
      ) : (
        !canManage && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            Brak skanu
          </span>
        )
      )}

      {canManage && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              handlePick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            title={pickButtonTitle}
            className={
              compact
                ? "p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
                : "inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline disabled:opacity-50"
            }
          >
            {uploading ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : scan ? (
              <RefreshCw size={13} />
            ) : (
              <Upload size={13} />
            )}
            {!compact && (uploading ? "Wysyłanie…" : scan ? "Podmień" : "Dodaj skan (PDF)")}
          </button>
          {scan && onDelete && (
            <button
              type="button"
              disabled={uploading}
              onClick={onDelete}
              title="Usuń skan"
              className={
                compact
                  ? "p-1 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 disabled:opacity-50"
                  : "inline-flex items-center gap-1 text-xs text-red-400 hover:underline disabled:opacity-50"
              }
            >
              <Trash2 size={13} />
              {!compact && "Usuń"}
            </button>
          )}
        </>
      )}

      {(error ?? uploadError) && (
        <span className="text-xs text-red-600 dark:text-red-400 basis-full">
          {error ?? uploadError}
        </span>
      )}
    </div>
  );
}
